import { useState, useEffect } from "react";
import { Loader2, Trash2, X } from "lucide-react";
import { useClients } from "../../Hooks/useClients";
import ClientsHeader from "./components/ClientsHeader";
// import ClientsStats from './components/ClientsStats';
import ClientsFilters from "./components/ClientsFilters";
import ClientsTableRow from "./components/ClientsTableRow";
import { canAccess } from "../../utils/permissions";
import { UserGroupIcon } from "@heroicons/react/24/outline";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { clientService } from "../../services/clientService";
import { toast } from "react-hot-toast";

export default function Clients() {
  const [showStatusMenu, setShowStatusMenu] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState("All Clients");
  const [selectedClients, setSelectedClients] = useState([]);
  const [openMenuId, setOpenMenuId] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [clientIdToDelete, setClientIdToDelete] = useState(null);

  const queryClient = useQueryClient();
  const { clients, isLoading, error, refetch } = useClients();

  const canCreateClient = canAccess("create_client");
  const canDeleteClient = canAccess("delete_client");

  useEffect(() => {
    const handleClickOutside = () => setOpenMenuId(null);
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  const filteredClients = (clients || []).filter((client) => {
    const matchesStatus =
      selectedStatus === "All Clients" ||
      (selectedStatus === "Leads" && client.status === "Lead") ||
      (selectedStatus === "Active" && client.status === "Active");

    const searchLower = searchTerm.toLowerCase();
    const matchesSearch =
      client.name.toLowerCase().includes(searchLower) ||
      client.email.toLowerCase().includes(searchLower) ||
      (client.phone && client.phone.toLowerCase().includes(searchLower)) ||
      (client.fullAddress &&
        client.fullAddress.toLowerCase().includes(searchLower));

    return matchesStatus && matchesSearch;
  });

  const toggleClient = (id) => {
    setSelectedClients((prev) =>
      prev.includes(id) ? prev.filter((c) => c !== id) : [...prev, id]
    );
  };

  const deleteClientMutation = useMutation({
    mutationFn: (id) => clientService.deleteClient(id),
    onSuccess: () => {
      toast.success("Client deleted successfully");
      queryClient.invalidateQueries(["companyClients"]);
      setShowDeleteModal(false);
      setClientIdToDelete(null);
    },
    onError: (err) => {
      toast.error(err.response?.data?.message || "Failed to delete client");
    },
  });

  const handleDeleteClick = (id) => {
    setClientIdToDelete(id);
    setShowDeleteModal(true);
  };

  const confirmDelete = () => {
    if (clientIdToDelete) {
      deleteClientMutation.mutate(clientIdToDelete);
    }
  };

  if (!canAccess("view_clients")) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] p-8 text-center">
        <div className="w-20 h-20 bg-red-50 rounded-full flex items-center justify-center mb-6">
          <UserGroupIcon className="w-10 h-10 text-red-600" />
        </div>
        <h2 className="text-3xl font-black text-gray-900 mb-2 tracking-tight">Access Restricted</h2>
        <p className="text-gray-500 max-w-md font-medium">
          You don't have the required permissions to view clients.
        </p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-10 text-center">
        <p className="text-red-600">
          Failed to load clients. Please try again.
        </p>
      </div>
    );
  }

  return (
    <section className="min-h-screen text-sm">
      <div className="bg-white border-b border-gray-200 px-4 py-3">
        <ClientsHeader clients={filteredClients} canCreateClient={canCreateClient} />
        {/* <ClientsStats /> */}
      </div>

      <div className="px-4 py-5">
        <ClientsFilters
          selectedStatus={selectedStatus}
          setSelectedStatus={setSelectedStatus}
          showStatusMenu={showStatusMenu}
          setShowStatusMenu={setShowStatusMenu}
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          totalCount={filteredClients.length}
        />

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden text-xs">
          {isLoading ? (
            <div className="p-20 text-center">
              <Loader2 className="w-12 h-12 mx-auto text-teal-600 animate-spin" />
            </div>
          ) : filteredClients.length === 0 ? (
            <div className="p-20 text-center text-gray-500">
              <p className="text-lg font-medium">No clients found</p>
              <p className="text-sm mt-2">
                Try adjusting filters or add a new client
              </p>
            </div>
          ) : (
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200 text-xs font-medium text-gray-700">
                <tr>
                  <th className="text-left px-3 py-3">Name</th>
                  <th className="text-left px-3 py-3">Contact</th>
                  <th className="text-left px-3 py-3">Address</th>
                  <th className="text-left px-3 py-3">Type</th>
                  <th className="text-left px-3 py-3">Rating</th>
                  {/* <th className="text-left px-3 py-3">Status</th> */}
                  {/* <th className="text-left px-3 py-3">Last Activity</th> */}
                  <th className="w-8"></th>
                </tr>
              </thead>
              <tbody>
                {filteredClients.map((client) => (
                  <ClientsTableRow
                    key={client.id}
                    client={client}
                    isSelected={selectedClients.includes(client.id)}
                    onToggle={toggleClient}
                    openMenuId={openMenuId}
                    setOpenMenuId={setOpenMenuId}
                    onDelete={() => handleDeleteClick(client.id)}
                    canDelete={canDeleteClient}
                  />
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <>
          <div
            className="fixed inset-0 bg-black/50 z-[60]"
            onClick={() => setShowDeleteModal(false)}
          />
          <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
            <div className="bg-white rounded-xl shadow-2xl max-w-sm w-full p-6 animate-in fade-in zoom-in-95 duration-200 text-center">
              <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Trash2 className="w-6 h-6 text-red-600" />
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">
                Delete Client
              </h3>
              <p className="text-sm text-gray-500 mb-6">
                Are you sure you want to delete this client? all related data will be deleted as well. This action cannot
                be undone.
              </p>
              <div className="flex gap-3 mt-6">
                <button
                  onClick={() => setShowDeleteModal(false)}
                  className="flex-1 px-4 py-2.5 border border-gray-300 text-gray-700 rounded-lg text-sm font-semibold hover:bg-gray-50 transition"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmDelete}
                  disabled={deleteClientMutation.isPending}
                  className="flex-1 px-4 py-2.5 bg-red-600 text-white rounded-lg text-sm font-semibold hover:bg-red-700 transition flex items-center justify-center gap-2"
                >
                  {deleteClientMutation.isPending ? "Deleting..." : "Delete"}
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </section>
  );
}
